/**
 * Organization tenancy types.
 *
 * Mirrors `backend/app/models/organization.py`. Content (screenplays,
 * analyses) is still owned by users; an organization decides which features a
 * member sees, not which scripts they can read.
 */

export type OrgRole = 'owner' | 'admin' | 'billing' | 'member' | 'guest' | 'counsel';
export type OrgKind = 'personal' | 'company' | 'school';
export type OrgPlan = 'personal' | 'team' | 'enterprise';

export interface EnterprisePricingConfig {
  base_monthly?: number;
  module_prices?: Record<string, number>;
  seat_packs?: number;
  seat_pack_size?: number;
  seat_pack_price?: number;
  included_seats?: number;
  monthly_credits?: number;
  credit_rate?: number;
  total_monthly?: number;
}

export interface EnterpriseOrgSettings {
  selected_modules?: string[];
  enabled_modules?: string[];
  billing_cycle?: 'monthly' | 'annual';
  pricing?: EnterprisePricingConfig;
}

export interface Organization {
  id: number;
  slug: string;
  name: string;
  kind: OrgKind;
  plan: OrgPlan;
  seat_limit: number | null;
  storage_quota_mb: number | null;
  feature_flags: Record<string, boolean>;
  settings?: EnterpriseOrgSettings;
  my_role: OrgRole | null;
  is_personal: boolean;
  website_url?: string | null;
}

export interface OrgMember {
  user_id: number;
  email: string;
  display_name: string | null;
  org_role: OrgRole;
  status: 'active' | 'suspended';
  joined_at?: string | null;
}

export interface OrgInvitation {
  id: number;
  email: string;
  org_role: OrgRole;
  expires_at: string;
  accepted_at: string | null;
  revoked_at: string | null;
  /** Returned only when the invitation is created. Not recoverable later. */
  invite_token?: string;
  /** Redemption link, also only present on creation. */
  invite_url?: string;
  /** False when the invitation email bounced — fall back to the link. */
  email_sent?: boolean;
}

/** Seat consumption against the plan. `seat_limit: null` means unlimited. */
export interface OrgSeatUsage {
  seat_limit: number | null;
  seats_used: number;
  seats_pending: number;
  seats_available: number | null;
  member_count: number;
}

/** One seat's action in the team feed. */
export interface OrgActivityActor {
  id: number;
  display_name: string | null;
  email: string;
}

export interface OrgActivityEvent {
  id: string;
  event_type: string;
  entity_type: string | null;
  entity_id: string | null;
  payload: Record<string, unknown> | null;
  created_at: string;
  is_mine: boolean;
  actor: OrgActivityActor;
}

/** Enterprise surfaces unlock on plan, or on an explicit org/user flag. */
export function isEnterpriseOrg(org: Organization | null): boolean {
  return org?.plan === 'enterprise';
}

/**
 * Role predicates mirroring `_ROLE_PERMISSIONS` in `backend/app/core/authz.py`.
 * These only decide what the UI offers — the backend enforces the real answer.
 */
export function canManageMembers(org: Organization | null): boolean {
  return org?.my_role === 'owner' || org?.my_role === 'admin';
}

export function canEditOrg(org: Organization | null): boolean {
  return org?.my_role === 'owner' || org?.my_role === 'admin';
}

export type WebsiteImportStatus = 'queued' | 'running' | 'completed' | 'failed';
export type WebsiteImportBasis = 'explicit' | 'inferred';
export type WebsiteImportConfidence = 'high' | 'moderate' | 'limited';

export interface WebsiteImportEvidence {
  basis: WebsiteImportBasis;
  confidence: WebsiteImportConfidence;
  quote: string | null;
  source_url: string | null;
}

export interface WebsiteImportBudget {
  min: number | null;
  max: number | null;
  currency: string | null;
}

export interface WebsiteImportUnfilled {
  field: string;
  reason: string;
}

export interface MandateWebsiteSuggestions {
  organization_name: string | null;
  mandate_name: string | null;
  applicable_slate: string | null;
  supported_formats: string[] | null;
  preferred_audience: string | null;
  preferred_tone_content_boundaries: string | null;
  production_budget: WebsiteImportBudget | null;
  development_capacity: string | null;
  distribution_assumptions: string | null;
  explicit_exclusions: string[] | null;
  evidence: Record<string, WebsiteImportEvidence>;
  unfilled: WebsiteImportUnfilled[];
}

export interface OrganizationWebsiteImport {
  id: number;
  organization_id: number;
  status: WebsiteImportStatus;
  source_url: string | null;
  result: MandateWebsiteSuggestions | Record<string, unknown>;
  error_code: string | null;
  provider?: string | null;
  model?: string | null;
  created_at?: string | null;
  started_at?: string | null;
  finished_at?: string | null;
}

export const ORG_ROLE_LABELS: Record<OrgRole, string> = {
  owner: 'Owner',
  admin: 'Admin',
  billing: 'Billing',
  member: 'Member',
  guest: 'Guest',
  counsel: 'Counsel',
};

export const ORG_PLAN_LABELS: Record<OrgPlan, string> = {
  personal: 'Personal',
  team: 'Team',
  enterprise: 'Enterprise',
};

/** Roles an admin may assign. Ownership transfer is deliberately excluded. */
export const ASSIGNABLE_ORG_ROLES: OrgRole[] = [
  'admin',
  'billing',
  'member',
  'guest',
  'counsel',
];

export type InvitationState = 'pending' | 'accepted' | 'revoked' | 'expired';

export function invitationState(invitation: OrgInvitation): InvitationState {
  if (invitation.accepted_at) return 'accepted';
  if (invitation.revoked_at) return 'revoked';
  if (new Date(invitation.expires_at).getTime() <= Date.now()) return 'expired';
  return 'pending';
}

/** Guests are free, matching `count_billable_seats` on the backend. */
export function consumesSeat(role: OrgRole): boolean {
  return role !== 'guest';
}
